import { useEffect, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";

import { NvCloseBoxIcon, NvFullscreenIcon, NvHeroPictogram, NvPauseIcon, NvPlayIcon } from "@/components/navigator/nv-icons-internal";
import { safeHttpUrl } from "@/lib/safe-url";
import { cn } from "@/lib/utils";

import { PwcMark } from "./internal-parts";

// 데모 영상 재생 모달 (Figma ver.7 03-12-01): 헤더 데모 영상(썸네일 + 재생 칩)을 누르면 열려요. 재생 / 일시정지 · 진행 바 · 재생 시간 ·
// 재생 속도(0.5x ~ 2.0x, 기본 1.0x) · 전체 화면 · 닫기. 영상 파일(mp4 · webm · ogg)은 직접 제어하고, YouTube · Vimeo 같은 임베드는 그쪽 컨트롤을 써요.

const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];
const speedLabel = (rate: number) => `${Number.isInteger(rate) ? rate.toFixed(1) : rate}x`;

function clock(seconds: number) {
  const s = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function embedOf(url: string): string | null {
  const youtube = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{6,})/i)?.[1];
  if (youtube) return `https://www.youtube.com/embed/${youtube}?rel=0`;
  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/i)?.[1];
  if (vimeo) return `https://player.vimeo.com/video/${vimeo}`;
  return null;
}

const isFile = (url: string) => /\.(mp4|webm|ogg)(\?|$)/i.test(url);

// 영상이 없거나 아직 불러오지 못했을 때의 대표 이미지(기본형) — 영상 화면 예시
function Placeholder() {
  return (
    <div className="absolute inset-0 bg-[linear-gradient(135deg,#F3F4F6_14%,#FFF3EA_86%)]">
      <span className="absolute right-5 top-5 text-[12px] leading-[14px] text-[#8A8F98]">영상 화면 (예시)</span>
      <NvHeroPictogram width={220} height={206} className="absolute left-[calc(50%+5px)] top-[calc(50%-10px)] -translate-x-1/2 -translate-y-1/2" />
      <PwcMark width={70} className="absolute bottom-10 left-10" />
    </div>
  );
}

export function DemoVideoModal({ open, onOpenChange, url, agentName }: { open: boolean; onOpenChange: (open: boolean) => void; url: string; agentName: string }) {
  const safe = safeHttpUrl(url);
  const embed = safe ? embedOf(safe) : null;
  const file = !!safe && !embed && isFile(safe);
  const video = useRef<HTMLVideoElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  const [playing, setPlaying] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [rate, setRate] = useState(1);
  const [menu, setMenu] = useState(false);

  useEffect(() => {
    if (!open) {
      setPlaying(false);
      setMenu(false);
    }
  }, [open]);

  useEffect(() => {
    if (video.current) video.current.playbackRate = rate;
  }, [rate, loaded]);

  const toggle = () => {
    const v = video.current;
    if (!v) return;
    if (v.paused) void v.play().catch(() => setPlaying(false));
    else v.pause();
  };
  const seek = (event: React.MouseEvent<HTMLDivElement>) => {
    const v = video.current;
    if (!v || !duration) return;
    const box = event.currentTarget.getBoundingClientRect();
    v.currentTime = Math.min(1, Math.max(0, (event.clientX - box.left) / box.width)) * duration;
  };
  const fullscreen = () => {
    const el = stage.current;
    if (!el) return;
    if (document.fullscreenElement) void document.exitFullscreen();
    else void el.requestFullscreen?.();
  };
  const pct = duration ? Math.min(100, (time / duration) * 100) : 0;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-[rgb(26_28_33_/_0.45)]" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className="fixed left-1/2 top-1/2 z-50 flex w-[960px] max-w-[calc(100vw-32px)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-[12px] bg-[#14161A] font-sans focus:outline-none"
        >
          <div className="flex h-14 items-center gap-4 px-6">
            <DialogPrimitive.Title className="text-[16px] font-bold leading-[19px] text-white">시연/데모 영상</DialogPrimitive.Title>
            <span className="min-w-0 flex-1 truncate text-[13px] leading-4 text-[#9BA0A8]">{agentName}</span>
            <DialogPrimitive.Close aria-label="닫기" className="text-white hover:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-nv-brand">
              <NvCloseBoxIcon />
            </DialogPrimitive.Close>
          </div>
          <div ref={stage} className="relative h-[540px] max-h-[calc(100vh-200px)] w-full bg-[#14161A]">
            {(!file || !loaded) && !embed && <Placeholder />}
            {embed && <iframe title={`${agentName} 시연/데모 영상`} src={embed} allow="fullscreen; picture-in-picture; autoplay" className="absolute inset-0 h-full w-full" />}
            {file && safe && (
              <video
                ref={video}
                src={safe}
                playsInline
                onLoadedData={() => setLoaded(true)}
                onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
                onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onEnded={() => setPlaying(false)}
                onClick={toggle}
                className={cn("absolute inset-0 h-full w-full bg-black object-contain", !loaded && "opacity-0")}
              />
            )}
          </div>
          {!embed && (
            <div className="relative h-16 px-6 pt-3">
              {menu && (
                <div role="menu" aria-label="재생 속도" className="absolute bottom-[calc(100%+8px)] right-[62px] z-10 w-[120px] rounded-[8px] bg-[#2A2D33] pb-[5px] pt-[5px] shadow-nv-popup">
                  <div className="px-[14px] pb-[6px] pt-[5px] text-[11px] leading-[13px] text-[#9BA0A8]">재생 속도</div>
                  {SPEEDS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      role="menuitemradio"
                      aria-checked={s === rate}
                      onClick={() => {
                        setRate(s);
                        setMenu(false);
                      }}
                      className={cn("flex h-[30px] w-full items-center justify-between px-[14px] text-left text-[13px] leading-4", s === rate ? "bg-white/[0.08] font-bold text-nv-brand-text" : "text-white hover:bg-white/[0.08]")}
                    >
                      {speedLabel(s)}
                      {s === rate && <span className="text-[12px] font-bold">✓</span>}
                    </button>
                  ))}
                </div>
              )}
              <div role="slider" aria-label="재생 위치" aria-valuemin={0} aria-valuemax={Math.round(duration)} aria-valuenow={Math.round(time)} tabIndex={0} onClick={seek} className="relative h-1 cursor-pointer rounded-full bg-white/20">
                <div className="h-full rounded-full bg-nv-brand" style={{ width: `${pct}%` }} />
                <span className="absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-nv-brand" style={{ left: `${pct}%` }} />
              </div>
              <div className="mt-[10px] flex h-7 items-center justify-between">
                <div className="flex items-center gap-4">
                  <button type="button" onClick={toggle} aria-label={playing ? "일시정지" : "재생"} className="inline-flex text-white hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-nv-brand">
                    {playing ? <NvPauseIcon /> : <NvPlayIcon />}
                  </button>
                  <span className="text-[13px] font-medium leading-4 text-white">
                    {clock(time)} / {clock(duration)}
                  </span>
                </div>
                <div className="flex items-center gap-[19px]">
                  <button type="button" aria-haspopup="menu" aria-expanded={menu} onClick={() => setMenu((v) => !v)} className="inline-flex h-6 w-[60px] items-center justify-center gap-[6px] rounded-[4px] bg-white/[0.16] text-[13px] font-medium leading-4 text-white">
                    {speedLabel(rate)}
                    <span aria-hidden className="text-[10px] leading-3">
                      {menu ? "▲" : "▼"}
                    </span>
                  </button>
                  <button type="button" onClick={fullscreen} aria-label="전체 화면" className="inline-flex text-white hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-nv-brand">
                    <NvFullscreenIcon />
                  </button>
                </div>
              </div>
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
